import clsx from 'clsx';
import { IconChevronRight, IconX } from '@tabler/icons-react';
import React, { type Ref } from 'react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

type InputLikeSelectTriggerProps = {
  ref?: Ref<HTMLButtonElement>;
  label?: string;
  current?: string;
  className?: string;
  onClick?: () => void;
  leftIcon?: React.ReactNode;
  isOpened?: boolean;
  showHasValue?: boolean;
  isLoading?: boolean;
  showAsterisk?: boolean;
  showCross?: boolean;
  onClickCross?: () => void;
};

export function InputLikeSelectTrigger({
  ref,
  label = '',
  current = '',
  isLoading = false,
  onClickCross,
  className,
  showAsterisk = false,
  showCross = false,
  onClick,
  showHasValue = false,
  leftIcon,
  isOpened = false,
  ...props
}: InputLikeSelectTriggerProps) {
  return (
    <>
      {isLoading ? (
        <Skeleton className={clsx('w-full rounded-[12px] h-[50px]', className)} />
      ) : (
        <Button
          ref={ref}
          {...props}
          type='button'
          variant='outline'
          onClick={onClick}
          className={clsx(
            'justify-between border-primary-stroke-day text-text-primary px-[8px] w-full h-[50px] rounded-[8px] bg-white hover:bg-white min-w-[260px] active:scale-100',
            'transition-[border-color] duration-150',
            className,
            {
              'border-primary-green-day!': !!isOpened,
              'border-primary!': showHasValue,
            },
          )}
        >
          {' '}
          <div className='flex gap-[8px] items-center'>
            {leftIcon && <div className='self-center '>{leftIcon}</div>}
            {label.length > 0 ? (
              <p className='shrink-0 leading-[14.4px] text-[16px] text-text-secondary'>{label}</p>
            ) : (
              <p className='shrink-0 leading-[14.4px] text-[16px] text-primary'>{current}</p>
            )}
            {showAsterisk && <span className='text-error'>*</span>}
          </div>
          <div className='flex min-w-0 text-[16px] items-center justify-self-end'>
            {current && label.length > 0 && <p className='truncate'>{current}</p>}
            {showCross ? (
              <span
                role='button'
                tabIndex={0}
                aria-label='Clear selected value'
                className='mt-[2px] flex shrink-0 cursor-pointer items-center justify-center text-icon-default'
                onPointerDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onClickCross?.();
                }}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter' && e.key !== ' ') {
                    return;
                  }

                  e.preventDefault();
                  e.stopPropagation();
                  onClickCross?.();
                }}
              >
                <IconX />
              </span>
            ) : (
              <IconChevronRight
                className={clsx(
                  'mt-[2px] text-icon-default transition-transform duration-200 rtl:-scale-x-100',
                  isOpened && 'rotate-90',
                )}
              />
            )}
          </div>
        </Button>
      )}
    </>
  );
}
